import { ComponentPropsWithoutRef, useContext, useEffect, useRef } from 'react';
import { FieldPath, FieldValues } from 'react-hook-form';
import { FilterContext } from 'components/filter/filters-next/FilterContext';
import { FilterFormContext } from 'components/filter/filters-next/FilterFormContext';
import { FilterFormValue } from 'components/filter/types';
import * as Form from 'components/form';
import { twMerge } from 'tailwind-merge';

interface FilterFormProps<T extends FieldValues>
    extends Omit<ComponentPropsWithoutRef<'form'>, 'onSubmit'> {
    path?: FieldPath<T>;
}

const FilterForm = <T extends FilterFormValue<any>>(props: FilterFormProps<T>) => {
    const { className = '', children, path = 'value' as FieldPath<T>, ...rest } = props;
    const { handleSubmit, trigger } = Form.useFormContext<T>();
    const { createOrUpdateFilter, deleteFilter } = useContext(FilterFormContext);
    const { clear, setIsOpen } = useContext(FilterContext);
    const didSubmitRef = useRef(false);
    const createOrUpdateFilterRef = useRef(createOrUpdateFilter);
    const deleteFilterRef = useRef(deleteFilter);

    useEffect(() => {
        const createOrUpdateFilter = createOrUpdateFilterRef.current;
        const deleteFilter = deleteFilterRef.current;

        return () => {
            if (!didSubmitRef.current) {
                trigger(path).then(isValid => {
                    if (isValid) {
                        handleSubmit(values => {
                            createOrUpdateFilter(values);
                        })();
                    } else {
                        deleteFilter();
                    }
                });
            }
        };
    }, [handleSubmit, path, trigger]);

    return (
        <form
            className={twMerge('contents', className)}
            onSubmit={e => {
                return handleSubmit(values => {
                    didSubmitRef.current = true;
                    createOrUpdateFilter(values);
                    setIsOpen(false);
                    clear();
                })(e);
            }}
            {...rest}
        >
            {children}
        </form>
    );
};

export { FilterForm };
